import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { UsersService } from 'src/app/_services/admin/users.service';
import { ProfilesService } from 'src/app/_services/admin/profiles.service';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import { ModalServiceService } from 'src/app/_services/modal-service.service';
import { WebSocketsService } from 'src/app/_services/web-sockets.service';
import { ErrorUtil } from 'src/app/_utilities/error';

@Component({
  selector: 'app-manage-integrations',
  standalone: false,
  templateUrl: './manage-integrations.component.html',
  styleUrl: './manage-integrations.component.scss'
})
export class ManageIntegrationsComponent extends SubscriptionUtil implements OnInit, OnDestroy {
  translate: any;
  data: any;

  googleEnable: any;
  microsoftEnable: any;
  calendlyEnable: any;

  canConnectGoogleCalender: boolean = false;
  googleCalenderConnected: boolean = false;
  microsoftCalenderConnected: boolean = false;
  calendlyCalenderConnected: boolean = false;

  googleCalenderBtnClicked: boolean = false;
  microsoftCalenderBtnClicked: boolean = false;
  calendlyCalenderBtnClicked: boolean = false;

  constructor(
    private languageTranslateService: LanguageTranslateService,
    public modalRef: BsModalRef,
    private userService: UsersService,
    private profileService: ProfilesService,
    private auth: AuthenticationService,
    private modalService: ModalServiceService,
    private webSocket: WebSocketsService,
    private errorUtil: ErrorUtil,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getLanguageData();
    this.initFromProfile();
    this.subscribeCalendarRefresh();
  }

  private initFromProfile() {
    const profile = this.data;
    if (!profile) {
      return;
    }

    this.googleCalenderConnected = !!profile.googleCalenderConnected;
    this.microsoftCalenderConnected = !!profile.microsoftCalenderConnected;
    this.calendlyCalenderConnected = !!profile.calendlyCalenderConnected;
    this.canConnectGoogleCalender = String(profile.id) === String(this.auth.getUserId());

    if (profile.profileId) {
      this.loadProfileIntegrations(profile.profileId);
    }
  }

  private loadProfileIntegrations(profileId: any) {
    this.push(
      this.profileService.getProfileById(profileId).subscribe((profileData: any) => {
        this.googleEnable = null;
        this.microsoftEnable = null;
        this.calendlyEnable = null;
        profileData?.profileIntegrationAccessList?.forEach((element: any) => {
          if (element.tenantIntegrationConfig.integrationType == 'GOOGLE_CALENDAR') {
            this.googleEnable = element;
          }
          if (element.tenantIntegrationConfig.integrationType == 'MICROSOFT_OUTLOOK') {
            this.microsoftEnable = element;
          }
          if (element.tenantIntegrationConfig.integrationType == 'CALENDLY') {
            this.calendlyEnable = element;
          }
        });
        this.cdr.markForCheck();
      })
    );
  }

  private subscribeCalendarRefresh() {
    this.push(
      this.webSocket.refreshGCelenderMessages$.subscribe((res: any) => {
        if (res?.message == 'SUCCESS') {
          this.errorUtil.setErrorMessage(
            200,
            this.translate?.setup.users.access.messages.calendarConnectedSuccessfully,
            null,
            'success',
            3000
          );
        } else {
          this.errorUtil.setErrorMessage(
            200,
            this.translate?.setup.users.access.messages.calendarConnectionNotSuccessful,
            null,
            'error',
            3000
          );
        }
        this.refreshIntegrationStatus();
      })
    );

    this.push(
      this.userService.refreshGoogleCalender$.subscribe(() => {
        this.refreshIntegrationStatus();
      })
    );

    this.push(
      this.userService.refreshMicrosoftCalender$.subscribe(() => {
        this.refreshIntegrationStatus();
      })
    );

    this.push(
      this.userService.refreshCalendlyCalender$.subscribe(() => {
        this.refreshIntegrationStatus();
      })
    );
  }

  /** Reload connection flags from API, then notify user-details-v2 */
  private refreshIntegrationStatus() {
    const userId = this.data?.id;
    if (!userId) {
      return;
    }

    this.push(
      this.userService.getUserProfile(userId).subscribe((profile: any) => {
        this.googleCalenderConnected = !!profile?.googleCalenderConnected;
        this.microsoftCalenderConnected = !!profile?.microsoftCalenderConnected;
        this.calendlyCalenderConnected = !!profile?.calendlyCalenderConnected;

        if (this.data) {
          this.data.googleCalenderConnected = this.googleCalenderConnected;
          this.data.microsoftCalenderConnected = this.microsoftCalenderConnected;
          this.data.calendlyCalenderConnected = this.calendlyCalenderConnected;
        }

        this.cdr.detectChanges();
        this.userService.refreshList$.emit({ type: 'integrations' });
      })
    );
  }

  get hasAnyIntegrationEnabled(): boolean {
    return !!(this.googleEnable?.enabled || this.microsoftEnable?.enabled || this.calendlyEnable?.enabled);
  }

  /******************************* Connect And Disconnect Google *****************************/
  connectGoogleCalender() {
    this.googleCalenderBtnClicked = true;
    this.microsoftCalenderBtnClicked = false;
    this.calendlyCalenderBtnClicked = false;
    this.push(
      this.userService.getGoogleCalenderConnectionUrl().subscribe((res: any) => {
        if (res?.webRedirectUrl) {
          window.open(
            res.webRedirectUrl,
            'google-calender',
            'width=600,height=600,scrollbars=no,resizable=no,toolbar=no,location=no,directories=no,status=no,menubar=no,copyhistory=no,'
          );
        }
      })
    );
  }

  disConnectGoogleCalender() {
    this.googleCalenderBtnClicked = true;
    this.microsoftCalenderBtnClicked = false;
    this.calendlyCalenderBtnClicked = false;
    this.modalService.openModal('DisconnectGoogleCalenderConfmationComponent', '');
  }

  /******************************* Connect And Disconnect Microsoft *****************************/
  connectMicrosoftCalender() {
    this.microsoftCalenderBtnClicked = true;
    this.googleCalenderBtnClicked = false;
    this.calendlyCalenderBtnClicked = false;
    this.push(
      this.userService.getMicrosoftCalenderConnectionUrl().subscribe((res: any) => {
        if (res?.webRedirectUrl) {
          window.open(
            res.webRedirectUrl,
            'Microsoft-calender',
            'width=600,height=600,scrollbars=no,resizable=no,toolbar=no,location=no,directories=no,status=no,menubar=no,copyhistory=no,'
          );
        }
      })
    );
  }

  disConnectMicrosoftCalender() {
    this.microsoftCalenderBtnClicked = true;
    this.googleCalenderBtnClicked = false;
    this.calendlyCalenderBtnClicked = false;
    this.modalService.openModal('DisconnectMicrosoftCalenderConfmationComponent', '');
  }

  /******************************* Connect And Disconnect Calendly *****************************/
  connectCalendlyCalender() {
    this.calendlyCalenderBtnClicked = true;
    this.googleCalenderBtnClicked = false;
    this.microsoftCalenderBtnClicked = false;
    this.push(
      this.userService.getCalendlyCalenderConnectionUrl().subscribe((res: any) => {
        if (res?.webRedirectUrl) {
          window.open(
            res.webRedirectUrl,
            'Microsoft-calender',
            'width=600,height=600,scrollbars=no,resizable=no,toolbar=no,location=no,directories=no,status=no,menubar=no,copyhistory=no,'
          );
        }
      })
    );
  }

  disConnectCalendlyCalender() {
    this.calendlyCalenderBtnClicked = true;
    this.googleCalenderBtnClicked = false;
    this.microsoftCalenderBtnClicked = false;
    this.modalService.openModal('DisconnectCalendlyConfmationComponent', '');
  }

  onClickIntegrations() {
    this.modalRef.hide();
    this.router.navigate(['dashboard/setup/integration/config']);
  }

  /********************************** Start Language **************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
        }
      })
    );
  }
  /********************************** End Language **************************/

  push(obs: any) {
    super.push(obs);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
